import { gcsOriginForWidth } from './photos';

/**
 * Custom `next/image` loader — **origin selection**, not a bypass.
 *
 * The obvious move here is to serve our GCS derivatives straight to the browser
 * and skip `/_next/image` entirely, since they are already sized and already
 * carry `Cache-Control: public, max-age=2592000`. That was measured against the
 * deployed Cloud Run instance and it **loses**:
 *
 * | at a 384 px card box        | mean bytes | TTFB p50 | p90 |
 * |-----------------------------|-----------|----------|-----|
 * | direct GCS `thumb`          | 26.5 KB   | 224 ms   | 293 ms |
 * | `/_next/image` cold (1st hit) | **15.5 KB** | 260 ms | **281 ms** |
 * | `/_next/image` warm         | **15.5 KB** | **149 ms** | **162 ms** |
 *
 * | at a 1080 px mobile hero    | mean bytes | TTFB p50 |
 * |-----------------------------|-----------|----------|
 * | direct GCS `large`          | 193.3 KB  | 187 ms   |
 * | `/_next/image` cold         | **94.0 KB** | 315 ms |
 *
 * Re-encoding to WebP is worth **41% of the bytes at card widths and 51% at
 * hero widths**, for ~36 ms of cold latency that disappears on the second hit —
 * with the legacy bucket's 30-day `Cache-Control`, repeat visits were a
 * browser-cache hit either way. Owned media uses a shorter upstream cache TTL
 * because numbered photo paths can be overwritten. Going direct would make
 * a 14-image search page ~150 KB heavier. (The measurement that suggested
 * otherwise predates the WebP-only + corrected-`sizes` changes: AVIF encoding
 * was 3–4× WebP's server time, and `sizes="33vw"` was pulling a 640 px source
 * for a 268 px box.)
 *
 * What *was* wasteful is which source the optimizer had to chew on. Every card
 * handed it the 1200 × 799 `large`. This loader hands it the 400 × 266 `thumb`
 * whenever the requested frame fits, which is byte-identical out and 9× fewer
 * pixels in. Anything that is not one of our derivatives — a raw MLS original
 * on a freshly-ingested listing, a GCS `original` on a listing with no
 * derivatives yet — passes through untouched, because for those the optimizer's
 * resize is the whole point.
 *
 * (`.svg` never reaches here: `next/image` marks SVG `unoptimized` upstream
 * unless `dangerouslyAllowSVG` is set, so the placeholder is served as-is.)
 *
 * **Wire this with the per-image `loader` prop, never `images.loaderFile`.**
 * Setting `loader: 'custom'` in `next.config` makes Next remove the
 * `/_next/image` route altogether — verified, it 404s — and every URL this
 * function returns points straight back at it.
 */
interface LoaderArgs {
  src: string;
  width: number;
  quality?: number;
}

function createPhotoLoader(maxWidth?: number) {
  return function photoLoader({ src, width, quality }: LoaderArgs): string {
    const w = maxWidth ? Math.min(width, maxWidth) : width;
    const origin = gcsOriginForWidth(src, w) ?? src;
    return `/_next/image?url=${encodeURIComponent(origin)}&w=${w}&q=${quality ?? 75}`;
  };
}

/** Heroes, lightbox, anything the buyer looks *at*: no cap, they want the pixels. */
export default createPhotoLoader();

/**
 * Result-card photos, capped at **828 px**.
 *
 * A card photo is painted into roughly 270–390 CSS px. On a DPR-3 phone the
 * browser therefore asks for a 1080 px frame, and that is where the mobile
 * weight was going — measured 69.8 KB from a GCS `large` and **103.3 KB** from
 * a raw MLS original, *per card*. Capping at 828 (≈2× a 390 px phone) costs
 * 31–35% fewer bytes (69.8 → 48.2 KB, 103.3 → 66.8 KB) and is not visible at
 * card size; screenshot-checked at 390 px against the uncapped build.
 *
 * The `srcset` descriptor still advertises the wider candidate, so the browser
 * upscales ~30% in the last step. That is the entire trade, and it is the
 * conventional DPR cap — it applies only to cards, never to a hero or the
 * lightbox, where the buyer is actually inspecting the photo.
 */
export const cardPhotoLoader = createPhotoLoader(828);
